import { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { type BibleBook } from '@/lib/utils/bible-utils';
import { cn } from '@/lib/utils/utils';
import { useMediaQuery } from '@/lib/hooks/use-media-query';
import { PassageSelectorContent } from '@/components/passage-selector-content';

interface PassageSelectorProps {
    /** Current book to highlight */
    currentBook: BibleBook;
    /** Current chapter to highlight */
    currentChapter: number;
    /** Current passage/section ID to highlight */
    currentPassageId?: string | null;
    /** Trigger button content */
    children: React.ReactNode;
}

export function PassageSelector({ currentBook, currentChapter, currentPassageId, children }: PassageSelectorProps) {
    const [open, setOpen] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 768px)');

    // Prevent body scroll when popover is open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    // Keyboard shortcut: Cmd+G (Mac) or Ctrl+G (Windows/Linux) to open passage selector
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'g') {
                e.preventDefault();
                setOpen(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Render Popover for both mobile and desktop
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="ghost" className="gap-2">
                    {children}
                    <ChevronDown className={cn(
                        "h-4 w-4 opacity-50 transition-transform duration-200",
                        open && "-rotate-90"
                    )} />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className={cn(
                    "p-0 z-[150]",
                    isDesktop ? "w-[500px]" : "min-w-[330px] max-h-[calc(100vh-48px)] overflow-hidden"
                )}
                align="start"
                side="bottom"
                sideOffset={0}
                alignOffset={isDesktop ? 0 : -16}
                onOpenAutoFocus={(e) => {
                    if (!isDesktop) {
                        e.preventDefault();
                    }
                }}
            >
                {open && (
                    <PassageSelectorContent
                        currentBook={currentBook}
                        currentChapter={currentChapter}
                        currentPassageId={currentPassageId}
                        isDesktop={isDesktop}
                        onClose={() => setOpen(false)}
                    />
                )}
            </PopoverContent>
        </Popover>
    );
}

